import { logger } from "@origin-digital/reporting-client";
import { AccountDetails_viewer } from "../../kraken/graphql-types/AccountDetails";
import { useKrakenAccountDetails } from "../../kraken/queries";
import {
  FieldType,
  FIELD_UNSUPPORTED,
  ProfileFieldMapResult,
  ProfileFieldMap,
} from "../../profileFieldMap";
import { useUpdateContactPreference } from "../../kraken/mutations";
import { useUpdateLpgAccountProfileContactPreference } from "../../lpg/mutations";
import { ContactPreferenceProp } from "../../../screens/UpdateContactPrefsScreen/ContactOption";
import { CommsDeliveryPreference } from "../../kraken/graphql-types/globalTypes";
import { LpgProfileAccountDetails_viewer as LpgViewer } from "../../lpg/graphql-types/LpgProfileAccountDetails";
import { mapToContactPreferenceProps } from "../kraken/krakenDataConverter";
import { useLpgAccountDetails } from "../../lpg/queries";
import { mapFieldsWithKraken } from "../kraken/krakenProfileFieldMap";
import {
  mapBillingDetailsToContactPreferenceProp,
  mapToLpgContactPreferenceProps,
} from "../lpg/lpgDataConverter";
import { DeliveryType } from "../../lpg/graphql-types/globalTypes";
import { ProfileFieldMapTypes } from "../../profileFieldMapContextHelpers";

export const mapFieldsWithMultiCrms = (
  viewer?: AccountDetails_viewer | null,
  lpgViewer?: LpgViewer | null
): ProfileFieldMap | undefined => {
  const lpgAccount = lpgViewer?.accounts?.[0];
  const lpgData = mapToLpgContactPreferenceProps(lpgAccount);

  // Try to process Kraken data, but don't fail if it errors
  let krakenFieldMapDefinition: ProfileFieldMap | undefined;
  let krakenData: ContactPreferenceProp[] = [];

  try {
    if (viewer) {
      krakenFieldMapDefinition = mapFieldsWithKraken(viewer);
      krakenData = mapToContactPreferenceProps(viewer) || [];
    }
  } catch (error) {
    logger.warn("mapFieldsWithMultiCrms: Error processing Kraken data", error);
    // Continue to process LPG data even if Kraken fails
  }

  let multiCrmsData: ContactPreferenceProp[] = [];

  if (krakenData) {
    multiCrmsData = [...krakenData];
  }
  if (lpgData) {
    multiCrmsData = [...multiCrmsData, ...lpgData];
  }

  if (krakenFieldMapDefinition) {
    return {
      ...krakenFieldMapDefinition,
      [FieldType.CONTACT_PREFERENCE]: {
        data: multiCrmsData,
        useUpdate: () => {
          const [update, { data, loading, error }] =
            useUpdateContactPreference();

          const [
            updateLpg,
            { data: updatelpgData, loading: lpgLoading, error: lpgError },
          ] = useUpdateLpgAccountProfileContactPreference();

          return [
            (value: ContactPreferenceProp) => {
              if (!value.preference) {
                return;
              }
              if (value.type === ProfileFieldMapTypes.LPG) {
                return updateLpg({
                  variables: {
                    accountNumber: value.id!,
                    preference: DeliveryType[value.preference],
                  },
                });
              } else {
                return update({
                  variables: {
                    accountNumber: value.id!,
                    commsDeliveryPreference:
                      CommsDeliveryPreference[value.preference],
                  },
                });
              }
            },
            {
              success:
                !!data?.updateCommsDeliveryPreference?.account
                  ?.commsDeliveryPreference ||
                !!updatelpgData?.updateAccount?.billing?.type,
              error: error || lpgError,
              loading: loading || lpgLoading,
              data: mapBillingDetailsToContactPreferenceProp(
                updatelpgData?.updateAccount?.billing
              ),
            },
          ];
        },
        editable: true,
      },
    };
  }

  // Keep contact preferences available for LPG accounts when Kraken is down.
  if (lpgData && lpgData.length > 0) {
    return {
      [FieldType.NAME]: FIELD_UNSUPPORTED,
      [FieldType.DOB]: FIELD_UNSUPPORTED,
      [FieldType.EMAIL]: FIELD_UNSUPPORTED,
      [FieldType.MOBILE]: FIELD_UNSUPPORTED,
      [FieldType.LANDLINE]: FIELD_UNSUPPORTED,
      [FieldType.HOME_PHONE]: FIELD_UNSUPPORTED,
      [FieldType.BILLING_ADDRESS]: FIELD_UNSUPPORTED,
      [FieldType.CONTACT_PREFERENCE]: {
        data: lpgData,
        useUpdate: () => {
          const [updateLpg, { data, loading, error }] =
            useUpdateLpgAccountProfileContactPreference();

          return [
            (value: ContactPreferenceProp) => {
              if (!value.preference) {
                return;
              }
              return updateLpg({
                variables: {
                  accountNumber: value.id!,
                  preference: DeliveryType[value.preference],
                },
              });
            },
            {
              success:
                data === undefined
                  ? undefined
                  : !!data?.updateAccount?.billing?.type,
              error,
              loading,
              data: mapBillingDetailsToContactPreferenceProp(
                data?.updateAccount?.billing
              ),
            },
          ];
        },
        editable: true,
      },
      [FieldType.BUSINESS_ACCOUNT]: FIELD_UNSUPPORTED,
    };
  }

  return undefined;
};

export const useMultiCrmsProfileFieldMap: () => ProfileFieldMapResult = () => {
  const { data, refetch, error, loading } = useKrakenAccountDetails();
  const {
    data: lpgData,
    refetch: lpgRefetch,
    error: lpgError,
    loading: lpgLoading,
  } = useLpgAccountDetails();
  if (lpgError && !loading && !lpgLoading) {
    logger.warn("useMultiCrmsProfileFieldMap() threw an LPG error", lpgError);
  }

  const fieldMap = mapFieldsWithMultiCrms(data?.viewer, lpgData?.viewer);
  const hasLpgContactPreferences =
    !!fieldMap?.[FieldType.CONTACT_PREFERENCE]?.data?.length;

  // Suppress Kraken error if LPG is still loading or if we have LPG data to show
  const canRecoverFromKrakenError =
    !!error && (lpgLoading || hasLpgContactPreferences);

  return {
    fieldMap,
    refetch: (type?: ProfileFieldMapTypes) => {
      if (type === ProfileFieldMapTypes.LPG) {
        lpgRefetch();
      }
      if (type === ProfileFieldMapTypes.KRAKEN) {
        refetch();
      }
    },
    error: canRecoverFromKrakenError ? undefined : error,
    loading: loading || lpgLoading,
  };
};
